import { Box, Stack, Theme } from '@mui/material';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import Text from '../../text';
import { createUseStyles } from 'react-jss';
import Panel from '@/components/panel';
import PanelTitle from '@/components/panel/panel-title';
import PanelContent from '@/components/panel/panel-content';
import Icon from '@/components/icon';
import Tooltip from '@/components/tooltip';
import ScrollAnchor from '@/components/scroll-anchor';
import { ScrollAnchorId } from '@/components/scroll-anchor/helpers';
import { IconType } from '@/components/icon/helpers';
import OnOffToggleButton from '../on-off-toggle-button';
import FormStatusChip from '@/components/chip/form-status-chip';
import { FormSectionStatus } from './helpers';

interface Props {
  title: string;
  status?: FormSectionStatus;
  children: ReactNode;
  locked?: boolean;
  disabled?: boolean;
  tooltip?: string;
  icon?: IconType;
  scrollId?: ScrollAnchorId;
  open?: boolean;
  onOpen?: (open: boolean) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  formSection: {
    position: 'relative',
    marginBottom: 30,
    '&.disabled': {
      backgroundColor: theme.palette.lightGray,
    },
    '& span.tooltip-children': {
      lineHeight: '70%',
    },
  },
  lockOverlay: {
    backgroundColor: theme.palette.gray,
    opacity: 0.5,
  },
  openToggle: {
    '& .MuiFormControl-root': {
      margin: 0,
    },
  },
}));

const FormSection: FC<Props> = ({
  children,
  title,
  status,
  locked,
  disabled,
  tooltip,
  icon,
  scrollId,
  open,
  onOpen,
}) => {
  const styles = useStyles();
  const hasStatus = !!status;
  const hasScrollId = !!scrollId;
  const canOpen = typeof open === 'boolean';
  const panelDisabled = disabled || locked || (canOpen && !open);
  const compClass = classNames(styles.formSection, { disabled: panelDisabled });

  const handleOpen = (value: boolean) => {
    onOpen?.(!!value);
  };

  return (
    <Panel className={compClass}>
      {hasScrollId && <ScrollAnchor scrollId={scrollId} />}
      <PanelTitle disabled={panelDisabled}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" flexGrow={1} alignItems="center" gap={1}>
            {icon && <Icon name={icon} size="small" />}
            <Text size="large">{title}</Text>
            {hasStatus && <FormStatusChip status={status} />}
          </Stack>
          {locked && (
            <Tooltip title={tooltip} disabled={!tooltip}>
              <Icon name="lock" size="small" />
            </Tooltip>
          )}
          {canOpen && (
            <Box className={styles.openToggle}>
              <OnOffToggleButton
                value={open}
                onChange={handleOpen}
                withNotSet={false}
                name="form-section-toggle"
                fullWidth={false}
                size="small"
              />
            </Box>
          )}
        </Stack>
      </PanelTitle>
      <Box position="relative">
        <PanelContent>{children}</PanelContent>
        {canOpen && !open && (
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            right={0}
            zIndex={1}
            className={styles.lockOverlay}
          />
        )}
      </Box>
    </Panel>
  );
};

export default FormSection;
